import { PARTS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Typography, Divider } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../../../contexts/auth";
import { formatDateTime } from "../../../../utils";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

export const PartBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(PARTS_ENDPOINT);
  const [part, setPart] = useState<any | null>(null);
  const canViewParts = useHasPermission("service.view_part");

  useEffect(() => {
    const fetchPart = async () => {
      if (id) {
        const response = await get(id);
        setPart(response.data);
      }
    };

    const handlePartUpdated = () => {
      fetchPart();
    };

    window.addEventListener("partUpdated", handlePartUpdated);
    fetchPart();
    return () => {
      window.removeEventListener("partUpdated", handlePartUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!canViewParts) {
    return null;
  }

  if (!part) return <CustomCircularProgress />;

  return (
    <Box
      sx={basicInfoContainerStyle}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {part.name}
        </Typography>
      </Box>

      <Divider />

      {/* Basic Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <DetailRow label={t("Code")}>
            <Typography variant="subtitle1">{part.code || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Name")}>
            <Typography variant="subtitle1">{part.name || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Unit")}>
            <Typography variant="subtitle1">{part.unit || "-"}</Typography>
          </DetailRow>
          <DetailRow label={t("Stock Quantity")}>
            <Typography variant="subtitle1">{part.stock_quantity || "0"}</Typography>
          </DetailRow>
          {part.notification_threshold && (
            <DetailRow label={t("Notification Threshold")} noBorder>
              <Typography variant="subtitle1">{part.notification_threshold}</Typography>
            </DetailRow>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Metadata Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Metadata")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Created At")}:</strong> {formatDateTime(part.created_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Updated At")}:</strong> {formatDateTime(part.updated_at)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
